import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useFlow } from 'context/FlowContext';
import { saveQuizRestrictions, saveQuizTargets } from 'services/publicFlowService';
import RestrictionSearch from 'components/storefront/RestrictionSearch';
import { restrictionLabel } from 'utils/memberRestrictions';
import { commitTargetEdit, sumMealTargets, distributeDailyTargets } from 'utils/macroTargets';
import { MacroTargetCard, MacroTargetEditor, MacroField, MealRestrictionEditor, cardLinkStyle } from 'components/shared/MacroTargets';

// Only the daily editor lists these now — everything read-only is drawn by
// MacroTargetCard, which owns its own ordering and units.
const MACRO_FIELDS = [
  { key: 'calories', label: 'Calories (kcal)' },
  { key: 'proteinG', label: 'Protein (g)' },
  { key: 'carbsG', label: 'Carbs (g)' },
  { key: 'fatG', label: 'Fat (g)' },
  { key: 'fiberG', label: 'Fiber (g)' },
];

const MEAL_SPLIT = {
  Breakfast: 0.25,
  Lunch: 0.35,
  Dinner: 0.30,
  Snacks: 0.10,
};

// MEAL_CAPS, caloriesFromMacros and reconcileMacros moved to utils/macroTargets
// when the account panel gained the same editor — two copies of Claire's caps
// would have drifted the moment either side changed. The cards and the meal
// editor below are the account panel's Nutrition Profile, shared the same way.

// caloricDelta/macroAdjustment come back parenthetical ("(we decreased...)")
// since that's how they're embedded mid-sentence in the email — e.g. "Overall:
// X kcals/day (we decreased...)". Shown standalone here instead, so strip the
// parens and make it read as its own sentence. Backend text is untouched;
// this is display-only, email still gets the original parenthetical form.
function asStandaloneSentence(text) {
  if (!text) return '';
  let s = text.trim();
  if (s.startsWith('(') && s.endsWith(')')) s = s.slice(1, -1).trim();
  if (!s) return '';
  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (!/[.!?]$/.test(s)) s += '.';
  return s;
}

function mealMacros(profile, meal, fraction) {
  const real = profile.mealBreakdown?.[meal];
  if (real) return real;
  // Fallback only for older submissions without mealBreakdown data
  return {
    calories: Math.round((profile.calories || 2000) * fraction),
    proteinG: Math.round((profile.proteinG || 100) * fraction),
    carbsG: Math.round((profile.carbsG || 225) * fraction),
    fatG: Math.round((profile.fatG || 67) * fraction),
    fiberG: Math.round((profile.fiberG || 25) * fraction),
  };
}

export default function QuizResultsPage() {
  const { householdMembers, activeMemberId, updateHouseholdMember } = useFlow();
  const location = useLocation();
  const result = location.state?.result;
  const memberId = location.state?.memberId || activeMemberId;

  const member = householdMembers.find((m) => m.id === memberId);

  const [editingMacros, setEditingMacros] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [draftProfile, setDraftProfile] = useState(null);
  const [draftMeals, setDraftMeals] = useState(null);
  const [draftTags, setDraftTags] = useState(null);
  const [draftLabels, setDraftLabels] = useState(null);
  // Per-meal overrides the customer has set explicitly; meals without an entry
  // stay derived from the daily totals via MEAL_SPLIT.
  const [editingMeal, setEditingMeal] = useState(null);
  const [draftMeal, setDraftMeal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  const [tagError, setTagError] = useState('');
  const [mealTagDraft, setMealTagDraft] = useState(null); // { meal, ids, labels, allMeals }

  // A hidden-nutrition result carries no profile, so it still counts as a
  // completed quiz here or it would bounce straight back.
  if (!member?.hasFullQuiz && !result?.profile && !result?.hideNutrition) {
    return <Navigate to="/quiz" replace />;
  }

  // Ops tagged the client "No Nutrition Data in Sheet"; the server withheld the
  // profile, so there is nothing to render and nothing to edit.
  const hideNutrition = member?.hideNutrition ?? Boolean(result?.hideNutrition);
  const hasNutritionist = member?.hasNutritionist ?? Boolean(result?.hasNutritionist);
  const p = member?.profile || result?.profile;

  const allTags = [
    ...(member?.restrictionTags || []),
    ...(p?.restrictions || []),
  ].filter((t, i, arr) => t && t !== 'none' && arr.indexOf(t) === i);

  // The meals as currently shown: an explicit per-meal edit wins, otherwise the
  // quiz's own breakdown (or the MEAL_SPLIT fallback for older submissions).
  // Both the daily total and the trickle-down below work from this, so what
  // the customer sees adding up is exactly what gets redistributed.
  const mealNames = Object.keys(p?.mealBreakdown || MEAL_SPLIT);
  const snackCount = p?.mealBreakdown?.Snacks?.count || 1;
  const effectiveMeals = p
    ? Object.fromEntries(mealNames.map((meal) => [
      meal, p.mealTargets?.[meal] || mealMacros(p, meal, MEAL_SPLIT[meal] ?? 0),
    ]))
    : {};
  // Summed from the meals rather than read off the profile: saving one meal
  // used to leave the Daily total card untouched (SFLANDING-95).
  const dailyTotals = p ? sumMealTargets(effectiveMeals, snackCount) : null;

  const startEditMacros = () => {
    setDraftProfile({ ...dailyTotals });
    setDraftMeals(null);
    setSaveError('');
    setEditingMacros(true);
  };

  // Same Client rows the account panel edits. No row ids (offline quiz
  // fallback) means there is nothing in Airtable to update, so it stays local.
  // Resolves to the profile's rowTargets as now stored, or null on failure.
  const persistMeals = async (meals) => {
    const rowIds = member?.clientRowIds || result?.clientRowIds || [];
    if (!rowIds.length) return p?.rowTargets || {};
    setSaving(true);
    setSaveError('');
    try {
      const { createdIds = [], rowTargets = {} } = await saveQuizTargets(rowIds, meals);
      // New rows for meals the quiz skipped; later saves must update them, not recreate.
      if (createdIds.length) updateHouseholdMember(memberId, { clientRowIds: [...rowIds, ...createdIds] });
      return { ...(p?.rowTargets || {}), ...rowTargets };
    } catch (e) {
      setSaveError(e?.response?.data?.error || e.message || 'Could not save.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Each daily edit is split across the meals straight away and the field shows their sum,
  // so what the customer sees is exactly what saves.
  const commitDaily = (key, raw) => {
    const next = commitTargetEdit(draftProfile, key, raw);
    if (next === draftProfile) return;
    const meals = distributeDailyTargets(draftMeals || effectiveMeals, next, snackCount, key === 'calories' ? 'calories' : 'macros');
    setDraftMeals(meals);
    setDraftProfile(sumMealTargets(meals, snackCount));
  };

  const saveMacros = async () => {
    if (draftMeals) {
      const rowTargets = await persistMeals(draftMeals);
      if (!rowTargets) return;
      updateHouseholdMember(memberId, {
        profile: { ...p, ...sumMealTargets(draftMeals, snackCount), mealTargets: draftMeals, rowTargets },
      });
    }
    setEditingMacros(false);
    setDraftProfile(null);
  };

  // The quiz writes one set to every meal, so a meal without its own edit shows that set.
  const mealTags = (meal) => member?.restrictionsByMeal?.[meal] || allTags;
  const labelsNow = member?.restrictionLabels || p?.restrictionLabels || {};

  const startEditMealTags = (meal) => {
    const ids = mealTags(meal);
    setMealTagDraft({ meal, ids: [...ids], labels: { ...labelsNow }, allMeals: false });
    setTagError('');
  };

  const saveMealTags = async () => {
    const { meal, ids, labels, allMeals } = mealTagDraft;
    const rowIds = member?.clientRowIds || result?.clientRowIds || [];
    if (rowIds.length) {
      setSavingTags(true);
      setTagError('');
      try {
        await saveQuizRestrictions(rowIds, ids, allMeals ? undefined : meal);
      } catch (e) {
        setTagError(e?.response?.data?.error || e.message || 'Could not save your allergies.');
        return;
      } finally {
        setSavingTags(false);
      }
    }
    const byMeal = Object.fromEntries(mealNames.map((m) => [m, allMeals || m === meal ? ids : mealTags(m)]));
    // The storefront still filters per person, so it gets every meal's restrictions.
    const union = [...new Set(Object.values(byMeal).flat())];
    const mergedLabels = { ...labelsNow, ...labels };
    updateHouseholdMember(memberId, {
      restrictionsByMeal: byMeal,
      restrictionTags: union,
      restrictionLabels: mergedLabels,
      profile: { ...p, restrictions: union, restrictionLabels: mergedLabels },
    });
    setMealTagDraft(null);
  };

  const startEditTags = () => {
    setDraftTags([...allTags]);
    setDraftLabels({ ...(member?.restrictionLabels || p?.restrictionLabels || {}) });
    setTagError('');
    setEditingTags(true);
  };

  const saveTags = async () => {
    const rowIds = member?.clientRowIds || result?.clientRowIds || [];
    if (rowIds.length) {
      setSavingTags(true);
      setTagError('');
      try {
        await saveQuizRestrictions(rowIds, draftTags || []);
      } catch (e) {
        setTagError(e?.response?.data?.error || e.message || 'Could not save your allergies.');
        return;
      } finally {
        setSavingTags(false);
      }
    }
    updateHouseholdMember(memberId, {
      restrictionsByMeal: null,
      restrictionTags: draftTags,
      restrictionLabels: draftLabels,
      profile: { ...p, restrictions: draftTags, restrictionLabels: draftLabels },
    });
    setEditingTags(false);
    setDraftTags(null);
    setDraftLabels(null);
  };

  return (
    <main className="sf-page">
      <div className="sf-page__inner">
        <p className="sf-page__eyebrow">Nutrition plan for {member?.name || 'you'}</p>
        <h1 className="sf-page__title">Your personalized plan</h1>
        <p className="sf-page__lead">
          {hideNutrition
            ? 'Every one of your meals will be adapted according to your food restrictions & sensitivities.'
            : 'Every one of your meals will be adapted according to these macro targets and food restrictions & sensitivities.'}
        </p>

        {/* No numeric targets exist for a hideNutrition client, so there's nothing
            for the nutrition team to review within 48 hours — matches AccountPanel's
            nutrition screen, which suppresses this same banner the same way. */}
        {!hideNutrition && (
          <p className="sf-results-disclaimer" role="note">
            <strong>These are preliminary results.</strong> Our nutrition team reviews
            every plan within 48 hours and will reach out if anything needs adjusting.
            <br></br>
            <br></br>
            You can keep editing your targets below, or <strong>log in with the email you used in the quiz </strong>(no registration needed), and you can view and update those targets from your account anytime.
          </p>
        )}

        {/* Stands in for the macro section — confirms the quiz landed without
            implying figures exist that we're choosing not to show. */}
        {hideNutrition && (
          <section className="sf-results-section">
            <p className="sf-page__lead">
              {hasNutritionist
                ? 'We are working with your nutritionist directly to create great meals just for you.'
                : 'We have all of your information and are all set to create meals designed for you.'}
            </p>
          </section>
        )}

        {/* Macros by meal section */}
        {!hideNutrition && (
        <section className="sf-results-section">
          <div className="sf-results-section__header">
            <h2 className="sf-results-section__title">Daily macro targets</h2>
            {!editingMacros ? (
              <button type="button" className="sf-results-edit-btn" onClick={startEditMacros}>
                Edit
              </button>
            ) : (
              <div className="sf-results-edit-actions">
                <button type="button" className="sf-results-edit-btn" onClick={saveMacros} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="sf-btn-text"
                  disabled={saving}
                  onClick={() => { setEditingMacros(false); setDraftProfile(null); setSaveError(''); }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Daily totals: the same card the account panel shows per meal, or
              the plain field grid while the customer is editing them. */}
          {editingMacros ? (
            <>
              <div className="sf-results-grid">
                {MACRO_FIELDS.map(({ key, label }) => (
                  <div key={key} className="sf-results-card">
                    <span className="sf-results-card__label">{label}</span>
                    <MacroField
                      className="sf-input sf-results-card__input"
                      value={draftProfile[key]}
                      onCommit={(raw) => commitDaily(key, raw)}
                    />
                  </div>
                ))}
              </div>
              {saveError && <p className="sf-page__meta" style={{ color: '#c0392b' }} role="alert">{saveError}</p>}
            </>
          ) : !p ? (
            <p className="sf-page__meta">We&apos;re still finalizing your macro targets.</p>
          ) : (
            <>
              <div className="sf-macro-cards sf-macro-cards--daily">
                <MacroTargetCard title="Daily total" targets={dailyTotals} />
              </div>

              {/* Breakdown by meal type */}
              <div className="sf-results-meal-breakdown">
                <p className="sf-results-meal-breakdown__lead">Per meal type — edit any meal to set its own targets</p>
                <div className="sf-macro-cards">
                  {mealNames.map((meal) => {
                    const m = effectiveMeals[meal];
                    // How many snacks/day, straight from the quiz answer; `m` is per snack.
                    const snacksPerDay = p?.mealBreakdown?.Snacks?.count;
                    const mealTitle = meal === 'Snacks' && Number(snacksPerDay) > 0
                      ? `Snacks (${snacksPerDay} per day)`
                      : meal;

                    const editingTagsHere = mealTagDraft?.meal === meal;
                    return (
                      <MacroTargetCard
                        key={meal}
                        title={mealTitle}
                        targets={m}
                        note={p.mealSkipNotes?.[meal]}
                        restrictions={mealTags(meal).map((tag) => labelsNow[tag] || tag)}
                        targetsEditor={editingMeal === meal && (
                          <MacroTargetEditor
                            embedded
                            meal={meal}
                            draft={draftMeal}
                            onChange={setDraftMeal}
                            saveLabel={`Save ${meal.toLowerCase()}`}
                            saving={saving}
                            error={saveError}
                            onSave={async () => {
                              const rowTargets = await persistMeals({ [meal]: draftMeal });
                              if (!rowTargets) return;
                              const { snacksPerDay: newCount, ...targets } = draftMeal;
                              const meals = { ...(p.mealTargets || {}), [meal]: targets };
                              const allMeals = { ...effectiveMeals, [meal]: targets };
                              const countChanged = newCount != null && p.mealBreakdown?.Snacks;
                              updateHouseholdMember(memberId, {
                                profile: {
                                  ...p,
                                  ...sumMealTargets(allMeals, countChanged ? newCount : snackCount),
                                  mealTargets: meals,
                                  rowTargets,
                                  ...(countChanged ? {
                                    mealBreakdown: { ...p.mealBreakdown, Snacks: { ...p.mealBreakdown.Snacks, count: newCount } },
                                  } : {}),
                                },
                              });
                              setEditingMeal(null);
                              setDraftMeal(null);
                            }}
                            onCancel={() => { setEditingMeal(null); setDraftMeal(null); setSaveError(''); }}
                          />
                        )}
                        restrictionEditor={editingTagsHere && (
                          <MealRestrictionEditor
                            meal={meal}
                            draft={mealTagDraft}
                            onChange={setMealTagDraft}
                            onSave={saveMealTags}
                            onCancel={() => { setMealTagDraft(null); setTagError(''); }}
                            saving={savingTags}
                            error={tagError}
                            showAllMeals={mealNames.length > 1}
                          />
                        )}
                        restrictionFooter={!mealTagDraft && (
                          <div>
                            <button type="button" style={cardLinkStyle} onClick={() => startEditMealTags(meal)}>
                              Edit {meal.toLowerCase()} allergies
                            </button>
                          </div>
                        )}
                        footer={(
                          <button
                            type="button"
                            style={cardLinkStyle}
                            onClick={() => {
                              setEditingMeal(meal);
                              setDraftMeal(meal === 'Snacks' ? { ...m, snacksPerDay: Number(snacksPerDay) > 0 ? Number(snacksPerDay) : null } : { ...m });
                              setSaveError('');
                            }}
                          >
                            Edit {meal.toLowerCase()} targets
                          </button>
                        )}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </section>
        )}

        {/* Personalization summary — health goals / calorie adjustment / macro
            adjustment sentences, same source as the email's HEALTH_GOALS/
            CALORIC_DELTA_STATEMENT/MACRO_ADJUSTMENT_STATEMENT merge vars via
            personalizationSummary on the profile response. Not gated on
            hideNutrition here — caloricDelta already comes back empty for
            those clients (see get_personalization_summary), and healthGoals/
            macroAdjustment don't reference withheld figures. */}
        {p?.personalizationSummary && Object.values(p.personalizationSummary).some(Boolean) && (
          <section className="sf-results-section">
            <div className="sf-results-section__header">
              <h2 className="sf-results-section__title">How we personalized your plan</h2>
            </div>
            {p.personalizationSummary.acknowledgement && (
              <p style={{ lineHeight: 1.6, margin: '0 0 8px', fontWeight: 600 }}>{p.personalizationSummary.acknowledgement}</p>
            )}
            {p.personalizationSummary.healthGoals && (
              <p style={{ lineHeight: 1.6, margin: '0 0 8px' }}>
                You shared you want {p.personalizationSummary.healthGoals}.
              </p>
            )}
            {p.personalizationSummary.caloricDelta && (
              <p style={{ lineHeight: 1.6, margin: '0 0 8px' }}>{asStandaloneSentence(p.personalizationSummary.caloricDelta)}</p>
            )}
            {p.personalizationSummary.macroAdjustment && (
              <p style={{ lineHeight: 1.6, margin: 0 }}>{asStandaloneSentence(p.personalizationSummary.macroAdjustment)}</p>
            )}
          </section>
        )}

        {/* Nutrition tips — same source (Airtable's Recommendations Based on
            Profile/Health Goals, Where to find the nutrients you need, Foods
            to avoid) as the email's RECOMMENDATIONS_PROFILE/etc. merge vars,
            via nutritionTips on the profile response. Not reimplemented here —
            whatever text the sheet computed is what's shown, on both surfaces. */}
        {!hideNutrition && p.nutritionTips && Object.values(p.nutritionTips).some(Boolean) && (
          <section className="sf-results-section">
            <div className="sf-results-section__header">
              <h2 className="sf-results-section__title">Nutrition tips for you</h2>
            </div>
            {p.nutritionTips.recommendationsProfile && (
              <p style={{ whiteSpace: 'pre-line', lineHeight: 1.6, margin: '0 0 20px' }}>{p.nutritionTips.recommendationsProfile}</p>
            )}
            {p.nutritionTips.recommendationsHealthGoals && (
              <p style={{ whiteSpace: 'pre-line', lineHeight: 1.6, margin: '0 0 20px' }}>{p.nutritionTips.recommendationsHealthGoals}</p>
            )}
            {p.nutritionTips.nutrientsGuide && (
              <>
                <p style={{ fontWeight: 700, margin: '0 0 8px' }}>Where to find the nutrients you need</p>
                <p style={{ whiteSpace: 'pre-line', lineHeight: 1.6, margin: '0 0 20px' }}>{p.nutritionTips.nutrientsGuide}</p>
              </>
            )}
            {p.nutritionTips.foodsToAvoid && (
              <>
                <p style={{ fontWeight: 700, margin: '0 0 8px' }}>Foods to avoid</p>
                <p style={{ whiteSpace: 'pre-line', lineHeight: 1.6, margin: 0 }}>{p.nutritionTips.foodsToAvoid}</p>
              </>
            )}
          </section>
        )}

        {/* Only when there are no meal cards to carry the restrictions. */}
        {(hideNutrition || !p) && (
        <section className="sf-results-section">
          <div className="sf-results-section__header">
            <h2 className="sf-results-section__title">Food restrictions &amp; sensitivities</h2>
            {!editingTags ? (
              <button type="button" className="sf-results-edit-btn" onClick={startEditTags}>
                Edit
              </button>
            ) : (
              <div className="sf-results-edit-actions">
                <button type="button" className="sf-results-edit-btn" onClick={saveTags} disabled={savingTags}>
                  {savingTags ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="sf-btn-text"
                  disabled={savingTags}
                  onClick={() => { setEditingTags(false); setDraftTags(null); setTagError(''); }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {editingTags ? (
            <>
              <RestrictionSearch
                selected={draftTags || []}
                labelMap={draftLabels || {}}
                onChange={(ids, labels) => {
                  setDraftTags(ids);
                  setDraftLabels(labels);
                }}
              />
              {tagError && <p className="sf-page__meta" style={{ color: '#c0392b' }} role="alert">{tagError}</p>}
            </>
          ) : allTags.length > 0 ? (
            <div className="sf-results-tags">
              {allTags.map((tag) => (
                <span key={tag} className="sf-results-tag">{restrictionLabel(tag, member)}</span>
              ))}
            </div>
          ) : (
            <p className="sf-page__meta">No restrictions set. <button type="button" className="sf-btn-text" onClick={startEditTags}>Add restrictions</button></p>
          )}
        </section>
        )}

        <div className="sf-page__actions">
          <Link to="/this-weeks-menu" className="sf-btn-flow sf-btn-flow--primary">
            View my menu →
          </Link>
        </div>
      </div>
    </main>
  );
}
